import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import API from "Util/Axios";
import { getUser } from "Util/index";
import { policyAllow } from "components/Policy/allow";

// Session permissions from GET permisos/me. One refresh path feeds the new
// modular checks (can / canAct / allow) and the legacy keys that <Policy> reads.
//
// status: "loading" | "ready" | "fallback" | "error"
//   fallback: /me is not available (403 for customer-portal sessions, 404 when
//   the backend has no permisos routes yet), so the
//   legacy keys come from GET user/policies, exactly as before this change.

const FOCUS_THROTTLE_MS = 60 * 1000;
const POLL_MS = 5 * 60 * 1000;
const RANK = { no: 0, ver: 1, editar: 2 };
const EMPTY = [];

export const PermisosContext = createContext(null);

// T2/100A82: diagnóstico en la consola del navegador, solo con npm start (NODE_ENV=development).
const logPermisosDev = (status, superadmin, keys) => {
  if (process.env.NODE_ENV !== "development") return;
  console.log(
    `[PERMISOS] estado ${status} · superadmin ${superadmin} · llaves ${keys.length}` +
      ` · crm:view ${keys.includes("crm:view")} · roles:view ${keys.includes("roles:view")}`,
    keys.slice(0, 15)
  );
};

const toList = (v) => (v == null ? EMPTY : Array.isArray(v) ? v : [v]);

export const PermisosProvider = ({ children }) => {
  const [state, setState] = useState({ status: "loading", me: null, legacyKeys: EMPTY, error: null });
  const lastFetch = useRef(0);
  const loaded = useRef(false); // true once /me answered: later failures keep the last known data
  const mode = useRef("loading"); // "fallback" stops the polling; every other mode keeps retrying
  const issued = useRef(0); // sequence number of the latest request
  const applied = useRef(0); // sequence number of the latest response that was applied

  const refresh = useCallback(async () => {
    lastFetch.current = Date.now();
    const seq = ++issued.current;
    // A response older than one already applied is stale: drop it, success or failure.
    const isStale = () => seq < applied.current;
    try {
      // Customer-portal sessions have no /me (it answers 403): skip the call
      // and go straight to the legacy-keys fallback below.
      if (getUser()?.customer) throw Object.assign(new Error("portal session"), { response: { status: 403 } });
      const { data } = await API.get("permisos/me");
      if (isStale()) return;
      applied.current = seq;
      loaded.current = true;
      mode.current = "ready";
      // Safety net for C1: the server adds the "superadmin:all" sentinel the
      // cargos editor looks for; make sure it is there for a superadmin anyway.
      const keys = data.legacyKeys || EMPTY;
      const legacyKeys =
        data.superadmin && !keys.includes("superadmin:all") ? [...keys, "superadmin:all"] : keys;
      setState({ status: "ready", me: data, legacyKeys, error: null });
      logPermisosDev("ready", Boolean(data.superadmin), legacyKeys); // T2/100A82
    } catch (e) {
      if (isStale()) return;
      // A 403 means "this session has no /me" (customer portal) and a 404 means
      // the backend was deployed without the permisos routes. Both fall back to
      // the legacy keys. Any other failure keeps the current state and is
      // retried on focus and on the poll.
      const httpStatus = e?.response?.status;
      if (httpStatus !== 403 && httpStatus !== 404) {
        // T2/100A82: diagnóstico del error de permisos/me (solo desarrollo).
        if (process.env.NODE_ENV === "development") {
          console.error("[PERMISOS] permisos/me falló:", httpStatus, e?.response?.data || e?.message);
        }
        if (!loaded.current) {
          mode.current = "error";
          setState({ status: "error", me: null, legacyKeys: EMPTY, error: e });
        }
        return;
      }
      try {
        const { data } = await API.get("user/policies");
        if (isStale()) return;
        applied.current = seq;
        loaded.current = true;
        mode.current = "fallback";
        setState({ status: "fallback", me: null, legacyKeys: data || EMPTY, error: null });
        logPermisosDev("fallback", false, data || EMPTY); // T2/100A82
      } catch (err) {
        if (isStale() || loaded.current) return;
        mode.current = "error";
        setState({ status: "error", me: null, legacyKeys: EMPTY, error: err });
      }
    }
  }, []);

  useEffect(() => {
    refresh();
    const onFocus = () => {
      if (mode.current === "fallback") return;
      if (Date.now() - lastFetch.current < FOCUS_THROTTLE_MS) return;
      refresh();
    };
    const timer = setInterval(() => {
      if (mode.current !== "fallback") refresh();
    }, POLL_MS);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(timer);
    };
  }, [refresh]);

  const value = useMemo(() => {
    const { me, status, legacyKeys, error } = state;
    const modular = me?.model === "modular";
    const superadmin = Boolean(me?.superadmin);
    const levels = me?.levels || {};
    const actions = me?.actions || EMPTY;

    const can = (id, level = "ver") => (RANK[levels[id]] || 0) >= RANK[level];
    const canAct = (act) => actions.includes(act);
    const allow = ({ perm, level = "ver", act, legacy } = {}) => {
      // Superadmin implies everything, as on the server (requirePermiso). A
      // legacy superadmin's category never holds the keys of modules added later.
      if (superadmin) return true;
      if (modular && (perm || act)) {
        return Boolean(perm && can(perm, level)) || toList(act).some(canAct);
      }
      if (legacy === undefined) return superadmin;
      return policyAllow(legacy, legacyKeys);
    };

    return {
      status,
      error,
      model: me?.model || "legacy",
      catalogVersion: me?.catalogVersion,
      cargo: me?.cargo || null,
      profile: me?.cargo?.dashboardProfile || null,
      superadmin,
      levels,
      actions,
      legacyKeys,
      can,
      canAct,
      allow,
      refresh,
    };
  }, [state, refresh]);

  return <PermisosContext.Provider value={value}>{children}</PermisosContext.Provider>;
};

// Without a provider (isolated component tests) everything is denied except
// ungated checks.
const NO_PROVIDER = {
  status: "loading",
  model: "legacy",
  cargo: null,
  profile: null,
  superadmin: false,
  levels: {},
  actions: EMPTY,
  legacyKeys: EMPTY,
  can: () => false,
  canAct: () => false,
  allow: ({ legacy } = {}) => legacy !== undefined && policyAllow(legacy, EMPTY),
  refresh: async () => {},
};

export const usePermiso = () => useContext(PermisosContext) || NO_PROVIDER;
